import React, { useCallback, useMemo } from "react";
import { BetaTag } from "@design-system/BetaTag/BetaTag";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralize } from "@common/helpers/string";
import { AccrualImporterRow, AccrualMode } from "./types";
import {
    groupRowsByInvoice,
    useAccrualImporterState,
    AccrualImporterGroup,
} from "./useAccrualImporterState";
import { AccrualImporterSpreadsheet } from "./AccrualImporterSpreadsheet";
import { ImporterDrawerShell } from "@components/importer/ImporterDrawerShell";
import { useAccrualDefaultAccountSeeding } from "./useAccrualDefaultAccountSeeding";

interface Props {
    mode: AccrualMode;
    open?: boolean;
    onClose: () => void;
    onSubmit: (groups: AccrualImporterGroup[]) => Promise<void>;
    isSubmitting?: boolean;
    initialRows?: AccrualImporterRow[];
    title?: string;
    submitLabel?: string;
}

export const AccrualImporterDrawer: React.FC<Props> = ({
    mode,
    open = true,
    onClose,
    onSubmit,
    isSubmitting,
    initialRows,
    title: titleOverride,
    submitLabel,
}) => {
    const { rows, setRows, addLineItemToGroup, removeRow, createRow } =
        useAccrualImporterState(initialRows);
    useAccrualDefaultAccountSeeding({ rows, setRows, mode });
    const { error: showError } = useToaster();

    const isBill = mode === "bill";
    const accrualNoun = isBill ? "bill" : "invoice";
    const title =
        titleOverride ?? (isBill ? "Import Bills" : "Import Invoices");
    const submitButtonLabel = submitLabel ?? title;

    const isValidGroup = useCallback(
        (g: AccrualImporterGroup) =>
            g.lines.length > 0 && g.lines.some((l) => l.amount !== 0),
        [],
    );

    const handleSubmit = useCallback(async () => {
        const groups = groupRowsByInvoice(rows);
        const validGroups = groups.filter(isValidGroup);
        if (validGroups.length === 0) {
            showError("Enter at least one line item with a non-zero amount");
            return;
        }

        const missingEntity = validGroups.some((g) => !g.entityId);
        if (missingEntity) {
            showError("Entity is required for all bills/invoices");
            return;
        }

        try {
            await onSubmit(validGroups);
        } catch {
            showError("Failed to import. Please try again.");
        }
    }, [rows, onSubmit, showError, isValidGroup]);

    const filledRows = useMemo(
        () =>
            rows.filter(
                (r) =>
                    Boolean(r.amount) ||
                    Boolean(r.description) ||
                    Boolean(r.productOrService) ||
                    Boolean(r.invoiceNumber),
            ),
        [rows],
    );

    const accrualCount = useMemo(() => {
        const groups = groupRowsByInvoice(filledRows);
        return groups.filter(isValidGroup).length;
    }, [filledRows, isValidGroup]);

    return (
        <ImporterDrawerShell
            title={title}
            titleAddon={isBill && <BetaTag />}
            open={open}
            onClose={onClose}
            statusItems={[
                `${accrualCount} ${pluralize(accrualNoun, accrualCount)}`,
                `${filledRows.length} ${pluralize("line item", filledRows.length)}`,
            ]}
            submitLabel={submitButtonLabel}
            submitHotkeyDescription={`Import ${pluralize(accrualNoun, 2)}`}
            submitCount={accrualCount}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
        >
            <AccrualImporterSpreadsheet
                mode={mode}
                rows={rows}
                setRows={setRows}
                createRow={createRow}
                addLineItemToGroup={addLineItemToGroup}
                removeRow={removeRow}
                open={open}
            />
        </ImporterDrawerShell>
    );
};
